'use client';

import { useState } from 'react';
import { Box, Typography } from '@wishket/design-system';

import CollectionThumbnail from '../CollectionThumbnail';

import {
  MenuList,
  AuthorInfo,
  AIRecommendSection,
  CollectionCountSection,
} from './CollectionListCard.parts';

import { Collection } from '@/shared/types';

interface CollectionListCardProps
  extends Omit<Collection, 'scraps' | 'hashTags'> {
  count?: number;
  isOwner?: boolean;
  onClick?: () => void;
  onEditThumbnail?: () => void;
  onEditCollection: () => void;
  onDeleteCollection: () => void;
}

const CollectionListCard = ({
  title,
  profile,
  isOwner,
  thumbnail,
  description,
  thumbnailMainColor,
  count = 0,
  viewCount = 0,
  likeCount = 0,
  scrappedCount = 0,
  onClick,
  onEditThumbnail,
  onEditCollection,
  onDeleteCollection,
}: CollectionListCardProps) => {
  const [isMoreOptionsOpen, setIsMoreOptionsOpen] = useState(false);

  const handleMenuClick = () => {
    setIsMoreOptionsOpen(!isMoreOptionsOpen);
  };

  const handleEditThumbnail = () => {
    if (isMoreOptionsOpen) setIsMoreOptionsOpen(false);

    return onEditThumbnail && onEditThumbnail();
  };

  return (
    <Box
      onClick={() => onClick && onClick()}
      className="rounded-3xl border border-w-gray-100 shadow-modal"
    >
      <Box className="flex flex-col tablet:flex-row tablet:gap-8 tablet:p-8">
        <Box>
          <CollectionThumbnail
            size={{
              mobile: 'full',
              tablet: 'xl',
              desktop: 'xl',
            }}
            count={count}
            imgSrc={thumbnail}
            layerColor={thumbnailMainColor}
            onEdit={isOwner ? handleEditThumbnail : undefined}
          />
          {isOwner && (
            <Box className="tablet:hidden">
              <AIRecommendSection onClick={handleEditThumbnail} />
            </Box>
          )}
        </Box>
        <Box className="flex w-full flex-col justify-between p-5 tablet:max-w-[calc(100%_-_295px)] tablet:p-0 tablet:pt-0">
          <Box>
            <Box className="mb-1 flex items-start justify-between">
              <Typography
                variant="subTitle1"
                className="line-clamp-2 break-words font-medium text-w-gray-900 desktop:typo-title3"
              >
                {title}
              </Typography>
              {isOwner && (
                <Box className="hidden tablet:block">
                  <MenuList
                    isMoreOptionsOpen={isMoreOptionsOpen}
                    onMenuClick={handleMenuClick}
                    onEditCollection={onEditCollection}
                    onDeleteCollection={onDeleteCollection}
                  />
                </Box>
              )}
            </Box>
            <Box className="mb-1.5 flex items-center justify-between">
              <AuthorInfo {...profile} />
              {isOwner && (
                <Box className="tablet:hidden">
                  <MenuList
                    isMoreOptionsOpen={isMoreOptionsOpen}
                    onMenuClick={handleMenuClick}
                    onEditCollection={onEditCollection}
                    onDeleteCollection={onDeleteCollection}
                  />
                </Box>
              )}
            </Box>
            <Box className="mb-3 tablet:mb-0">
              <Typography
                variant="body2"
                className="line-clamp-2 text-w-gray-500"
              >
                {description}
              </Typography>
            </Box>
          </Box>
          <CollectionCountSection
            likeCount={likeCount}
            viewCount={viewCount}
            scrapCount={scrappedCount}
          />
        </Box>
      </Box>
      {isOwner && (
        <Box className="hidden tablet:block">
          <AIRecommendSection onClick={handleEditThumbnail} />
        </Box>
      )}
    </Box>
  );
};

export default CollectionListCard;
